import { DateDistanceDisplay, SimpleBarChart } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, ButtonGroup, Select } from '@ygtechnologies/rubus-ui';
import { FlatCard } from '@/components/FlatCard';
import { capitalize, startCase } from 'lodash-es';
import React, { useState } from 'react';
import {
    getChangeRequestStatistics,
    getCommentMessageStatistics,
    getCommentThreadStatistics,
    getSpending,
} from '@/services/statistics';
import { useAsyncState } from '@/utils/hooks';
import theme from './StatisticsSharedCards.module.scss';

interface StatisticsDashboardProps {
    projectIds: number[];
    // project: BygrProject;
}

const StatisticsSharedCards: React.FC<StatisticsDashboardProps> = ({ projectIds }) => {
    // Does not use it on bygr level. Only on project level
    const shouldLoadChangeRequests = projectIds.length > 0;
    const [changeRequestStatistics] = useAsyncState(
        shouldLoadChangeRequests ? () => getChangeRequestStatistics(projectIds) : () => Promise.resolve(null),
        projectIds,
        null
    );

    const [ctStats] = useAsyncState(() => getCommentThreadStatistics(projectIds), projectIds, null);
    const [cmStats] = useAsyncState(() => getCommentMessageStatistics(projectIds), projectIds, null);
    const [currencyId, setCurrencyId] = useState<number | undefined>(undefined);
    const [spending] = useAsyncState(() => getSpending(projectIds, currencyId), [...projectIds, currencyId], null);
    const [showSpendingTotal, setShowSpendingTotal] = useState(true);

    const currencyOptions = (spending?.currencies ?? []).map((c) => ({ value: c.id, label: c.code }));
    const selectedCurrency = spending?.currencies.find((c) => c.id === spending.selectedCurrencyId);

    function createRows<T extends {}>(data: T, ...excludeKeys: (keyof T)[]) {
        return Object.keys(data)
            .filter((k) => !(excludeKeys as string[]).includes(k))
            .map((k) => ({
                name: capitalize(startCase(k)),
                value: data[k],
            }));
    }

    const formatTotal = (x: number | undefined) => Math.round(x ?? 0).toLocaleString('en-GB');

    return (
        <>
            {shouldLoadChangeRequests && (
                <FlatCard title="Change requests by status" loading={!changeRequestStatistics}>
                    {() => (
                        <>
                            <SimpleBarChart
                                data={createRows(changeRequestStatistics!, 'total')}
                                nameKey="name"
                                valueKey="value"
                            />
                            <small className={theme.chartFooter}>Total: {formatTotal(changeRequestStatistics?.total)}</small>
                        </>
                    )}
                </FlatCard>
            )}

            <FlatCard title="Threads" loading={!ctStats}>
                {() => (
                    <>
                        <SimpleBarChart data={createRows(ctStats!, 'total')} nameKey="name" valueKey="value" />
                        <small className={theme.chartFooter}>Total: {formatTotal(ctStats?.total)}</small>
                    </>
                )}
            </FlatCard>
            <FlatCard title="Messages" loading={!cmStats}>
                {() => (
                    <>
                        <SimpleBarChart
                            data={createRows(cmStats!, 'total', 'lastSentMessage')}
                            nameKey="name"
                            valueKey="value"
                        />
                        <small className={theme.chartFooter}>
                            Last message sent: <DateDistanceDisplay value={cmStats!.lastSentMessage} />
                        </small>
                        <small className={theme.chartFooter}>Total: {formatTotal(cmStats?.total)}</small>
                    </>
                )}
            </FlatCard>
            <FlatCard
                title="Spending"
                actions={
                    <div className={theme.spendingActions}>
                        {currencyOptions.length > 1 && (
                            <Select
                                inputSize="xs"
                                aria-label="Currency"
                                options={currencyOptions}
                                value={spending?.selectedCurrencyId}
                                onChange={setCurrencyId}
                            />
                        )}
                        <ButtonGroup aria-label="Spending view">
                            <Button
                                size="xs"
                                variant={showSpendingTotal ? 'primary' : 'tertiary'}
                                onClick={() => setShowSpendingTotal(true)}>
                                Total
                            </Button>
                            <Button
                                size="xs"
                                variant={!showSpendingTotal ? 'primary' : 'tertiary'}
                                onClick={() => setShowSpendingTotal(false)}>
                                Average
                            </Button>
                        </ButtonGroup>
                    </div>
                }
                loading={!spending}>
                {() => (
                    <>
                        {showSpendingTotal ? (
                            <>
                                <SimpleBarChart
                                    data={[
                                        { name: 'Change requests', value: Math.round(spending!.changeRequests) },
                                        { name: 'Buyer options', value: Math.round(spending!.buyerOptions) },
                                    ]}
                                    nameKey="name"
                                    valueKey="value"
                                />
                                <small className={theme.chartFooter}>
                                    Total: {formatTotal(spending?.total)} {selectedCurrency?.code}
                                </small>
                            </>
                        ) : (
                            <>
                                <SimpleBarChart
                                    data={[
                                        {
                                            name: 'Change requests',
                                            value: Math.round(spending!.averageChangeRequestSpendingPerUnit),
                                        },
                                        {
                                            name: 'Buyer options',
                                            value: Math.round(spending!.averageBuyerOptionSpendingPerUnit),
                                        },
                                    ]}
                                    nameKey="name"
                                    valueKey="value"
                                />
                                <small className={theme.chartFooter}>
                                    Average per unit: {formatTotal(spending?.totalAverage)} {selectedCurrency?.code}
                                </small>
                            </>
                        )}
                    </>
                )}
            </FlatCard>
        </>
    );
};

export default StatisticsSharedCards;
